import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Trophy, ArrowLeft, WhatsappLogo } from '@phosphor-icons/react';
import LanguageToggle from '../components/LanguageToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = [
  { id: 'feminino', label: 'Feminino' },
  { id: 'open', label: 'Open' },
  { id: 'misto', label: 'Misto' },
  { id: 'rei_rainha', label: 'Rei e Rainha' },
];

function ScoreDisplay({ m }) {
  if (!m) return null;
  const done = m.score1 !== null && m.score1 !== undefined && m.score2 !== null && m.score2 !== undefined;
  const winner = done ? (m.score1 > m.score2 ? 'd1' : m.score2 > m.score1 ? 'd2' : null) : null;
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#F0EDE5] last:border-0 text-sm" data-testid={`public-match-${m.slot_id}`}>
      <span className={`flex-1 min-w-0 break-words leading-tight pr-2 ${winner === 'd1' ? 'font-bold text-[#2D6A4F]' : 'text-[#1A1A1A]'}`} title={m.dupla1}>{m.dupla1 || '-'}</span>
      <span className={`px-2 font-mono text-base flex-shrink-0 ${done ? 'font-bold text-[#E85D04]' : 'text-[#C8C4B8]'}`}>{done ? `${m.score1} × ${m.score2}` : '× × ×'}</span>
      <span className={`flex-1 min-w-0 break-words leading-tight text-right pl-2 ${winner === 'd2' ? 'font-bold text-[#2D6A4F]' : 'text-[#1A1A1A]'}`} title={m.dupla2}>{m.dupla2 || '-'}</span>
    </div>
  );
}

function GroupPublicCard({ groupId, groupData }) {
  const standings = groupData.standings || [];
  const matches = groupData.matches || [];
  return (
    <div className="bg-white border border-[#E6E2D8] rounded-xl p-4 space-y-3">
      <h4 className="font-bold text-[#1A1A1A] text-sm uppercase tracking-wider">Grupo {groupId}</h4>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[#73706A] border-b border-[#E6E2D8]">
            <th className="text-left py-1">#</th><th className="text-left">Dupla</th><th>J</th><th>V</th><th>D</th><th>Sal</th><th>PTS</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((r, i) => (
            <tr key={r.dupla} className="border-b border-[#F0EDE5]" data-testid={`public-standing-${groupId}-${i}`}>
              <td className="py-1 font-bold text-[#E85D04]">{i + 1}º</td>
              <td className="text-[#1A1A1A]">{r.dupla}</td>
              <td className="text-center">{r.j}</td><td className="text-center">{r.v}</td><td className="text-center">{r.d}</td>
              <td className="text-center">{r.saldo >= 0 ? '+' : ''}{r.saldo}</td>
              <td className="text-center font-bold text-[#E85D04]">{r.pts}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {matches.length > 0 && (
        <div className="pt-2 border-t border-[#F0EDE5]">
          <p className="text-[10px] uppercase font-semibold text-[#73706A] mb-1">Jogos disputados</p>
          {matches.filter(m => m.score1 !== undefined && m.score1 !== null).map(m => <ScoreDisplay key={m.slot_id} m={m} />)}
        </div>
      )}
    </div>
  );
}

function BracketSection({ title, matches }) {
  if (!matches || (Array.isArray(matches) && matches.length === 0)) return null;
  return (
    <div className="bg-white border border-[#E6E2D8] rounded-xl p-4">
      <h4 className="font-bold text-[#1A1A1A] text-sm uppercase tracking-wider mb-2">{title}</h4>
      {Array.isArray(matches) ? matches.map(m => <ScoreDisplay key={m.slot_id} m={m} />) : <ScoreDisplay m={matches} />}
    </div>
  );
}

function ReiRainhaPublicView({ data }) {
  const groups = data.groups || {};
  const b = data.bracket || {};
  return (
    <div className="space-y-6" data-testid="public-rei-rainha">
      {Object.entries(groups).map(([gid, gd]) => (
        <div key={gid} className="bg-white border border-[#E6E2D8] rounded-xl p-4 space-y-3">
          <h4 className="font-bold text-[#1A1A1A] text-sm uppercase tracking-wider">Grupo Geral — {(gd.players || []).length} jogadores</h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[#73706A] border-b border-[#E6E2D8]">
                <th className="text-left py-1">#</th><th className="text-left">Jogador</th><th>J</th><th>V</th><th>D</th><th>Sal</th>
              </tr>
            </thead>
            <tbody>
              {(gd.standings || []).map((r, i) => (
                <tr key={r.player} className={`border-b border-[#F0EDE5] ${i < 2 ? 'bg-orange-50' : ''}`} data-testid={`public-rr-standing-${gid}-${i}`}>
                  <td className="py-1 font-bold text-[#E85D04]">{i + 1}º</td>
                  <td className="text-[#1A1A1A]">{r.player}</td>
                  <td className="text-center">{r.j}</td><td className="text-center">{r.v}</td><td className="text-center">{r.d}</td>
                  <td className="text-center">{r.saldo >= 0 ? '+' : ''}{r.saldo}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pt-2 border-t border-[#F0EDE5] space-y-2">
            {(gd.rounds || []).map(rd => (
              <div key={rd.round}>
                <div className="flex items-center justify-between mb-0.5">
                  <p className="text-[10px] uppercase font-semibold text-[#73706A]">Rodada {rd.round}</p>
                  {rd.resting && rd.resting.length > 0 && <p className="text-[10px] text-[#73706A]">Descansa: {rd.resting.join(', ')}</p>}
                </div>
                {rd.games.map(gm => <ScoreDisplay key={gm.slot_id} m={{ slot_id: gm.slot_id, dupla1: gm.team1, dupla2: gm.team2, score1: gm.score1, score2: gm.score2 }} />)}
              </div>
            ))}
          </div>
        </div>
      ))}
      <section>
        <h3 className="font-['Outfit'] text-lg font-bold text-[#1A1A1A] mb-3">Final — 1º × 2º do grupo</h3>
        <div className="max-w-xl">
          <BracketSection title="Final" matches={b.final} />
        </div>
      </section>
    </div>
  );
}

function CategoryView({ cat, data }) {
  const groups = data.groups || {};
  const b = data.bracket || {};
  const t = data.config?.type;
  if (t && t.startsWith('rei_rainha')) return <ReiRainhaPublicView data={data} />;
  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-['Outfit'] text-lg font-bold text-[#1A1A1A] mb-3">Fase de Grupos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Object.entries(groups).map(([gid, gd]) => <GroupPublicCard key={gid} groupId={gid} groupData={gd} />)}
        </div>
      </section>
      <section>
        <h3 className="font-['Outfit'] text-lg font-bold text-[#1A1A1A] mb-3">Chaveamento</h3>
        {t === 'groups_2x4_semi' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BracketSection title="Semifinais" matches={b.semi} />
            <BracketSection title="Final" matches={b.final} />
            <BracketSection title="3º Lugar" matches={b.third} />
          </div>
        )}
        {t === 'groups_2_of_3_playin' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BracketSection title="Play-in" matches={b.playin} />
            <BracketSection title="Semifinais" matches={b.semi} />
            <BracketSection title="Final" matches={b.final} />
            <BracketSection title="3º Lugar" matches={b.third} />
          </div>
        )}
        {t === 'groups_fem_4_3_semi' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BracketSection title="Play-in (2ºA x 3ºB · 2ºB x 3ºA)" matches={b.playin} />
            <BracketSection title="Semifinais" matches={b.semi} />
            <BracketSection title="Final" matches={b.final} />
            <BracketSection title="3º Lugar" matches={b.third} />
          </div>
        )}
        {(t === 'groups_open_a8_b4' || t === 'groups_3grp_a8_b4') && (
          <div className="space-y-6">
            <div className="bg-orange-50 border-2 border-[#E85D04] rounded-xl p-4">
              <h4 className="font-bold text-[#E85D04] mb-3">SÉRIE A (1º-8º)</h4>
              {b.serie_a_seeds?.length > 0 && (
                <div className="bg-white p-3 rounded border border-[#E6E2D8] mb-3">
                  <p className="text-xs font-semibold text-[#73706A] mb-1">Classificação geral</p>
                  <ol className="text-xs text-[#1A1A1A] grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
                    {b.serie_a_seeds.map((r, i) => <li key={r.dupla}><span className="font-bold text-[#E85D04]">{i + 1}º</span> {r.dupla} <span className="text-[#73706A]">({r.v}v, saldo {r.saldo >= 0 ? '+' : ''}{r.saldo})</span></li>)}
                  </ol>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BracketSection title="Quartas (1x8, 2x7, 3x6, 4x5)" matches={b.serie_a?.quarter} />
                <BracketSection title="Semifinais" matches={b.serie_a?.semi} />
                <BracketSection title="Final" matches={b.serie_a?.final} />
              </div>
            </div>
            <div className="bg-blue-50 border-2 border-[#0077B6] rounded-xl p-4">
              <h4 className="font-bold text-[#0077B6] mb-3">SÉRIE B (1º-4º)</h4>
              {b.serie_b_seeds?.length > 0 && (
                <div className="bg-white p-3 rounded border border-[#E6E2D8] mb-3">
                  <p className="text-xs font-semibold text-[#73706A] mb-1">Classificação</p>
                  <ol className="text-xs text-[#1A1A1A] grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
                    {b.serie_b_seeds.map((r, i) => <li key={r.dupla}><span className="font-bold text-[#0077B6]">{i + 1}º</span> {r.dupla} <span className="text-[#73706A]">({r.v}v, saldo {r.saldo >= 0 ? '+' : ''}{r.saldo})</span></li>)}
                  </ol>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BracketSection title="Semifinais (1x4, 2x3)" matches={b.serie_b?.semi} />
                <BracketSection title="Final" matches={b.serie_b?.final} />
              </div>
            </div>
          </div>
        )}
        {t === 'groups_4_of_4_a_b' && (
          <div className="space-y-6">
            <div className="bg-orange-50 border-2 border-[#E85D04] rounded-xl p-4">
              <h4 className="font-bold text-[#E85D04] mb-3">SÉRIE A (1º-8º)</h4>
              {b.serie_a_seeds?.length > 0 && (
                <div className="bg-white p-3 rounded border border-[#E6E2D8] mb-3">
                  <p className="text-xs font-semibold text-[#73706A] mb-1">Classificação geral</p>
                  <ol className="text-xs text-[#1A1A1A] space-y-0.5">
                    {b.serie_a_seeds.map((r, i) => <li key={r.dupla}><span className="font-bold text-[#E85D04]">{i + 1}º</span> {r.dupla} <span className="text-[#73706A]">({r.pts}pts, saldo {r.saldo >= 0 ? '+' : ''}{r.saldo})</span></li>)}
                  </ol>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BracketSection title="Quartas" matches={b.serie_a?.quarter} />
                <BracketSection title="Semifinais" matches={b.serie_a?.semi} />
                <BracketSection title="Final" matches={b.serie_a?.final} />
                <BracketSection title="3º Lugar" matches={b.serie_a?.third} />
              </div>
            </div>
            <div className="bg-blue-50 border-2 border-[#0077B6] rounded-xl p-4">
              <h4 className="font-bold text-[#0077B6] mb-3">SÉRIE B (9º-16º)</h4>
              {b.serie_b_seeds?.length > 0 && (
                <div className="bg-white p-3 rounded border border-[#E6E2D8] mb-3">
                  <p className="text-xs font-semibold text-[#73706A] mb-1">Classificação</p>
                  <ol className="text-xs text-[#1A1A1A] space-y-0.5">
                    {b.serie_b_seeds.map((r, i) => <li key={r.dupla}><span className="font-bold text-[#0077B6]">{i + 9}º</span> {r.dupla} <span className="text-[#73706A]">({r.pts}pts, saldo {r.saldo >= 0 ? '+' : ''}{r.saldo})</span></li>)}
                  </ol>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BracketSection title="Quartas" matches={b.serie_b?.quarter} />
                <BracketSection title="Semifinais" matches={b.serie_b?.semi} />
                <BracketSection title="Final" matches={b.serie_b?.final} />
              </div>
            </div>
          </div>
        )}
        {t === 'groups_3_of_4_top8' && (
          <div className="space-y-3">
            {b.seeds?.length > 0 && (
              <div className="bg-white p-3 rounded border border-[#E6E2D8]">
                <p className="text-xs font-semibold text-[#73706A] mb-1">Classificação geral (top 8)</p>
                <ol className="text-xs text-[#1A1A1A] grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
                  {b.seeds.map((r, i) => <li key={r.dupla}><span className="font-bold text-[#E85D04]">{i + 1}º</span> {r.dupla} <span className="text-[#73706A]">({r.pts}pts)</span></li>)}
                </ol>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <BracketSection title="Quartas" matches={b.series?.quarter} />
              <BracketSection title="Semifinais" matches={b.series?.semi} />
              <BracketSection title="Final" matches={b.series?.final} />
              <BracketSection title="3º Lugar" matches={b.series?.third} />
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default function TournamentPublic() {
  const [state, setState] = useState(null);
  const [tab, setTab] = useState('feminino');
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tk-league/tournament`);
      setState(data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    // Auto-refresh every 30s so viewers see live updates without reloading
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-white border-b border-[#E6E2D8] px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-tk.png" alt="Logo" className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="font-['Outfit'] font-bold text-[#1A1A1A]">Tia Karol Sports</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login" className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium">Login</Link>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#73706A] hover:text-[#1A1A1A] mb-4">
          <ArrowLeft size={16} /> Voltar
        </Link>
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <Trophy size={32} className="text-[#E85D04]" weight="fill" />
            <div>
              <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">TK League — Torneio</h1>
              <p className="text-xs text-[#73706A]">Acompanhe os resultados ao vivo. Atualização automática a cada 30 segundos.</p>
            </div>
          </div>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`Acompanhe o Torneio TK League ao vivo! ${typeof window !== 'undefined' ? window.location.origin : ''}/tk-league/torneio-publico`)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-whatsapp-btn"
            className="inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-[#1EBE5A] transition-colors shadow-sm"
          >
            <WhatsappLogo size={20} weight="fill" /> Compartilhar
          </a>
        </div>
        <div className="flex gap-2 mb-6" data-testid="public-tournament-tabs">
          {CATS.map(c => (
            <button key={c.id} onClick={() => setTab(c.id)} className={`px-4 py-2 rounded-lg font-semibold text-sm ${tab === c.id ? 'bg-[#E85D04] text-white' : 'bg-white text-[#1A1A1A] border border-[#E6E2D8]'}`} data-testid={`public-tab-${c.id}`}>{c.label}</button>
          ))}
        </div>
        {loading && <p className="text-center text-[#73706A] py-8">Carregando...</p>}
        {!loading && state && <CategoryView cat={tab} data={state[tab]} />}
      </main>
    </div>
  );
}
